import React from 'react';
import { RocketLaunchIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';

const Stage1 = () => {
  return (
    <PlaybookSection
      title="Stage 1: Identify & Prioritise AI Opportunities"
      icon={<MagnifyingGlassIcon className="h-7 w-7 text-primary-light" />}
      heading="From 'Adoption' to 'Usefulness'"
    >
      <div className="space-y-8">
        {/* TL;DR */}
        <ExpandableCallout 
          title="TL;DR - What you'll accomplish in this stage"
          type="info"
          defaultOpen={true}
        >
          <ul className="space-y-2 text-gray-300">
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">1.</span>
              <span>Shift your team's mindset from "we need to use AI" to "where can AI solve real problems?"</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">2.</span>
              <span>Run a lightweight AI opportunity workshop to identify high-impact use cases</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">3.</span>
              <span>Create simple, practical AI usage guidelines (Green/Yellow/Red zones)</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">4.</span>
              <span>Build AI confidence into your existing culture rituals</span>
            </li>
          </ul>
        </ExpandableCallout>

        {/* What do I need to DO */}
        <ExpandableCallout 
          title="What do I need to DO in this stage?"
          type="tip"
          defaultOpen={true}
        >
          <ol className="space-y-3 text-gray-300">
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">1.</span>
              <span>Frame AI exploration as a hunt for friction, not tools</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">2.</span>
              <span>Run a 24-hour async AI opportunity hackathon using our Miro template</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">3.</span>
              <span>Document Green/Yellow/Red AI usage guidelines (1-page max)</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">4.</span>
              <span>Set up regular AI demo sessions and sharing channels</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-3">5.</span>
              <span>Identify 3-5 quick win AI use cases to demonstrate what's possible</span>
            </li>
          </ol>
        </ExpandableCallout>

        {/* The Problem */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">The Problem</h3>
          <p className="text-gray-300 mb-4">
            When leadership says "let's adopt AI," everyone hears something different. For some it's exciting. 
            For others? Existential dread. Cue analysis paralysis.
          </p>
          <p className="text-gray-300 mb-4">
            Most teams either want to explore AI or are leveraging it under the radar anyway. They just don't know:
          </p>
          <ul className="space-y-2 text-gray-300 mb-6">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>What's allowed?</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>What's expected?</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Where do we even start?</span>
            </li>
          </ul>
          <p className="text-gray-300">
            And meanwhile, your People team is running at 120%. You don't have time to run a company-wide 
            AI transformation project. And you <em>shouldn't have to</em>.
          </p>
        </PlaybookCard>

        {/* The Shift */}
        <PlaybookCard>
          <div className="flex items-start mb-4">
            <MagnifyingGlassIcon className="h-6 w-6 text-primary-light mr-3 mt-1 flex-shrink-0" />
            <h3 className="text-xl font-semibold text-white">The Shift: From "Adoption" to "Usefulness"</h3>
          </div>
          <p className="text-gray-300 mb-4">
            Instead of asking teams to <em>use AI</em>, help them get clearer on:
          </p>
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-4">
            <p className="text-white font-medium text-center">
              "Where do we have jobs to be done that AI is suited for?"
            </p>
          </div>
          <p className="text-gray-300">
            This changes the conversation from <em>tech-first</em> to <em>outcome-first</em>—and makes it easier 
            for teams to engage without fear.
          </p>
        </PlaybookCard>

        {/* Step 1: Start With Friction */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">Step 1: Start With: Where is the friction?</h3>
          <p className="text-gray-300 mb-4">
            Frame AI exploration as a hunt for <em>friction</em>, not tools. Encourage teams to ask:
          </p>
          <ul className="space-y-2 text-gray-300 mb-6">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>What tasks drain time, energy, or attention?</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Where are we copy/pasting the same thing, over and over?</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Where do we hit decision bottlenecks, or repeat internal comms?</span>
            </li>
          </ul>
          
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50 mb-6">
            <h4 className="font-semibold text-white mb-3">Framework: 3 Filters for Good AI Use Cases</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 text-white font-medium">Criteria</th>
                    <th className="text-left py-2 text-white font-medium">Question to Ask</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-b border-gray-800">
                    <td className="py-2 font-medium">Repetitive</td>
                    <td className="py-2">Does it happen 5+ times a week?</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-2 font-medium">High cognitive load</td>
                    <td className="py-2">Is it mentally draining or complex to do?</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium">Clear structure</td>
                    <td className="py-2">Does it follow a repeatable format?</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-gray-300 mt-3 text-sm">
              <strong>If you tick 2 of 3, these are prime 'custom GPT' candidates ✅</strong>
            </p>
          </div>
<PlaybookImage src="https://auth.openorg.fyi/storage/v1/object/public/resources/leverage-ai-for-your-org/custom%20gpt%20build.gif" alt="Building custom GPTs" caption="Building custom GPTs" />
          <p className="text-gray-300 mb-4">These are great <em>Jobs to Be Done</em> candidates for AI augmentation. Think:</p>
          <ul className="space-y-2 text-gray-300">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Drafting job descriptions → [Custom JD generator]</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Synthesising survey comments → [GPT-powered theme summariser]</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Responding to inbound support emails → [Auto triage assistant]</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Prepping interview feedback → [Scorecard-to-summary GPT]</span>
            </li>
          </ul>
        </PlaybookCard>

        {/* Step 2: AI Hackathon */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">Step 2: Run an AI Themed Hackathon</h3>
          <p className="text-gray-300 mb-4">
            Create a Miro board that teams can use to self-discover AI opportunities.
          </p>
          
          <ToolCallout
            tool="miro"
            label="Hackathon Starter Kit Template"
            href="https://miro.com/miroverse/hackathon-starter-kit/"
            description="Basecamp-style hackathon template with sections for brainstorming, voting, and sharing"
          />

          <div className="mt-6">
            <h4 className="font-semibold text-white mb-3">Suggested board sections:</h4>
            <ul className="space-y-2 text-gray-300 mb-6">
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>What processes take the most time?</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>Where are we seeing context switching or repetition?</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>What manual tasks could be improved with smart automation?</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3">—</span>
                <span>What are we <em>curious</em> to try AI for?</span>
              </li>
            </ul>
            
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
              <p className="text-white font-medium">
                Host this as a 24 hour asynchronous jam. Invite folks to spend 30mins or so whenever they want 
                during the 24 hours to contribute to the board.
              </p>
            </div>
            <p className="text-gray-300 mt-3">
              You'll get some great insights—and probably identify some surprising quick wins. 🎉
            </p>
          </div>
        </PlaybookCard>

        {/* Step 3: Guidelines */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">Step 3: Document Practical Guidelines On AI Usage</h3>
          <p className="text-gray-300 mb-4">
            This is a good moment to collaborate with Tech/Security. Also a great moment to not write a 224-page policy.
          </p>
          <p className="text-gray-300 mb-6">
            Start lightweight: what's 🟢 Green, 🟠 Yellow, 🔴 Red?
          </p>
          
          <div className="space-y-4 mb-6">
            <div className="flex items-start">
              <span className="inline-block w-4 h-4 bg-green-500 rounded-full mr-3 mt-1 flex-shrink-0"></span>
              <div>
                <p className="text-white font-medium">Green = safe to explore</p>
                <p className="text-gray-300 text-sm">Internal task helpers (e.g. summaries, drafts, prep work)</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="inline-block w-4 h-4 bg-yellow-500 rounded-full mr-3 mt-1 flex-shrink-0"></span>
              <div>
                <p className="text-white font-medium">Yellow = handle with care</p>
                <p className="text-gray-300 text-sm">Needs more "human-in-the-loop" → anything external-facing or making decisions</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="inline-block w-4 h-4 bg-red-500 rounded-full mr-3 mt-1 flex-shrink-0"></span>
              <div>
                <p className="text-white font-medium">Red = do not use</p>
                <p className="text-gray-300 text-sm">Customer data, personal info, financials in public models</p>
              </div>
            </div>
          </div>

          <CopyToNotion title="AI Usage Guidelines Template">
            <div className="space-y-4">
              <h4 className="font-bold">🟢 Green Zone - Safe to Explore</h4>
              <ul className="space-y-1 pl-4">
                <li>• Internal task automation (summaries, drafts, prep work)</li>
                <li>• Content ideation and brainstorming</li>
                <li>• Process documentation and optimization</li>
                <li>• Personal productivity tools</li>
              </ul>
              
              <h4 className="font-bold">🟠 Yellow Zone - Proceed with Caution</h4>
              <ul className="space-y-1 pl-4">
                <li>• External-facing communications (requires review)</li>
                <li>• Decision-making support (human final approval required)</li>
                <li>• Customer interaction assistance</li>
                <li>• Data analysis and insights</li>
              </ul>
              
              <h4 className="font-bold">🔴 Red Zone - Do Not Use</h4>
              <ul className="space-y-1 pl-4">
                <li>• Personal identifiable information (PII)</li>
                <li>• Customer data or financial information</li>
                <li>• Confidential business information</li>
                <li>• Final decision-making without human oversight</li>
              </ul>
            </div>
          </CopyToNotion>

          <p className="text-gray-300 mt-4">
            Make it a simple Notion 1-pager. Share it. Pin it. Loom it. Take stock in a month and see if it still holds.
          </p>
        </PlaybookCard>

        {/* Step 4: Culture Integration */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">Step 4: Build AI Confidence Into Culture Rituals</h3>
          <ul className="space-y-3 text-gray-300 mb-6">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Monthly <strong className="text-white">"AI Demos + Donuts"</strong> = show what's working (10 mins)</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Async <strong className="text-white">"GPTs We Made"</strong> channel in Slack</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span><strong className="text-white">"GPT MVP of the Month"</strong> for the most helpful internal agent</span>
            </li>
          </ul>
          
          <PlaybookImage 
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/leverage-ai-for-your-org/trophy.png" 
            alt="Trophy representing AI achievements and culture building" 
            caption="🎉 Celebrating AI wins helps build momentum and encourages experimentation across teams" 
          />
          
          <p className="text-gray-300">
            It's about <strong className="text-primary-light">#visibility</strong>, <strong className="text-primary-light">#safety</strong> and <strong className="text-primary-light">#momentum</strong>.
          </p>
        </PlaybookCard>

        {/* Quick Win Ideas */}
        <PlaybookCard>
          <h3 className="text-xl font-semibold text-white mb-4">Bonus: Quick Win Ideas to Kick Things Off</h3>
          <p className="text-gray-300 mb-4">
            Here are 5 places we've seen startups get instant traction with AI:
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 text-white font-medium">Team</th>
                  <th className="text-left py-3 text-white font-medium">GPT/Agent Use Case</th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                <tr className="border-b border-gray-800">
                  <td className="py-3 font-medium">Talent</td>
                  <td className="py-3">JD + outreach drafts from role cards</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="py-3 font-medium">CX</td>
                  <td className="py-3">Auto-tagging inbound emails + tone check replies</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="py-3 font-medium">Product Ops</td>
                  <td className="py-3">Summarising weekly tickets + surfacing blockers</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="py-3 font-medium">Sales Enablement</td>
                  <td className="py-3">Drafting follow-ups post-demo using call notes</td>
                </tr>
                <tr>
                  <td className="py-3 font-medium">People</td>
                  <td className="py-3">Synthesising survey themes + prep for QBRs</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <p className="text-gray-300 mt-4">
            Sometimes the best way to accelerate adoption is to <em>just show what's possible</em>.
          </p>
        </PlaybookCard>

        {/* Outcome */}
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Stage 1 Outcome</h3>
          <p className="text-gray-300 mb-4">
            By the end of this stage, you should have:
          </p>
          <ul className="space-y-2 text-gray-300 mb-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>A list of 10-15 potential AI use cases identified by your teams</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Simple, practical AI usage guidelines that people actually understand</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>Regular AI sharing rituals embedded in your culture</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>3-5 prioritized quick wins ready for Stage 2 implementation</span>
            </li>
          </ul>
          <p className="text-gray-300">
            <strong>Next:</strong> Move to <a href="/playbooks/leverage-ai-for-you-and-your-org/stage-2" className="text-primary hover:text-primary-light">Stage 2</a> to start building your first AI products.
          </p>
        </div>
      </div>
    </PlaybookSection>
  );
};

export default Stage1; 